import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SiteSettings {
  site_title?: string;
  site_description?: string;
  site_keywords?: string;
  og_image_url?: string;
  favicon_url?: string;
  twitter_handle?: string;
}

const FAVICON_VERSION = "20260416s";

const STATIC_FAVICON_ASSETS = {
  generic: `/favicon-32x32-${FAVICON_VERSION}.png`,
  shortcut: `/favicon-32x32-${FAVICON_VERSION}.png`,
  icon32: `/favicon-32x32-${FAVICON_VERSION}.png`,
  icon16: `/favicon-16x16-${FAVICON_VERSION}.png`,
  appleTouch: `/apple-touch-icon-${FAVICON_VERSION}.png`,
  appleTouchPrecomposed: `/apple-touch-icon-precomposed-${FAVICON_VERSION}.png`,
};

interface IconLinkConfig {
  selector: string;
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
}

const getVersionedIconUrl = (url: string) => {
  if (typeof window === "undefined") return url;

  const parsedUrl = new URL(url, window.location.origin);
  parsedUrl.searchParams.set("v", FAVICON_VERSION);
  return parsedUrl.toString();
};

const isSameOriginOrRelativeIcon = (url: string) => {
  if (!url) return false;
  if (url.startsWith("/")) return true;
  if (typeof window === "undefined") return false;

  return new URL(url, window.location.origin).origin === window.location.origin;
};

const upsertIconLink = ({ selector, rel, href, sizes, type }: IconLinkConfig) => {
  let link = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    document.head.appendChild(link);
  }

  link.setAttribute("rel", rel);
  link.setAttribute("href", href);

  if (sizes) {
    link.setAttribute("sizes", sizes);
  } else {
    link.removeAttribute("sizes");
  }

  if (type) {
    link.setAttribute("type", type);
  } else {
    link.removeAttribute("type");
  }
};

export function SiteMetaHead() {
  const [settings, setSettings] = useState<SiteSettings>({});

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", [
          "site_title",
          "site_description",
          "site_keywords",
          "og_image_url",
          "favicon_url",
          "twitter_handle",
        ]);

      if (!error && data) {
        const settingsMap: SiteSettings = {};
        data.forEach((item) => {
          if (item.value) {
            settingsMap[item.key as keyof SiteSettings] = item.value;
          }
        });
        setSettings(settingsMap);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    // Update document title
    if (settings.site_title) {
      document.title = settings.site_title;
    }

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (settings.site_description) {
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.setAttribute("name", "description");
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute("content", settings.site_description);
    }

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (settings.site_keywords) {
      if (!metaKeywords) {
        metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute("content", settings.site_keywords);
    }

    // Update OG type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      document.head.appendChild(ogType);
    }
    ogType.setAttribute("content", "website");

    // Update OG url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute("content", window.location.origin);

    // Update OG image
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (settings.og_image_url) {
      if (!ogImage) {
        ogImage = document.createElement("meta");
        ogImage.setAttribute("property", "og:image");
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute("content", settings.og_image_url);

      // Add og:image:width
      let ogImageWidth = document.querySelector('meta[property="og:image:width"]');
      if (!ogImageWidth) {
        ogImageWidth = document.createElement("meta");
        ogImageWidth.setAttribute("property", "og:image:width");
        document.head.appendChild(ogImageWidth);
      }
      ogImageWidth.setAttribute("content", "1200");

      // Add og:image:height
      let ogImageHeight = document.querySelector('meta[property="og:image:height"]');
      if (!ogImageHeight) {
        ogImageHeight = document.createElement("meta");
        ogImageHeight.setAttribute("property", "og:image:height");
        document.head.appendChild(ogImageHeight);
      }
      ogImageHeight.setAttribute("content", "630");

      // Add og:image:type
      let ogImageType = document.querySelector('meta[property="og:image:type"]');
      if (!ogImageType) {
        ogImageType = document.createElement("meta");
        ogImageType.setAttribute("property", "og:image:type");
        document.head.appendChild(ogImageType);
      }
      const imageType = settings.og_image_url.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
      ogImageType.setAttribute("content", imageType);
    }

    // Update OG title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (settings.site_title) {
      if (!ogTitle) {
        ogTitle = document.createElement("meta");
        ogTitle.setAttribute("property", "og:title");
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute("content", settings.site_title);
    }

    // Update OG description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (settings.site_description) {
      if (!ogDescription) {
        ogDescription = document.createElement("meta");
        ogDescription.setAttribute("property", "og:description");
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute("content", settings.site_description);
    }

    // Update Twitter card type
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement("meta");
      twitterCard.setAttribute("name", "twitter:card");
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute("content", "summary_large_image");

    // Update Twitter image
    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (settings.og_image_url) {
      if (!twitterImage) {
        twitterImage = document.createElement("meta");
        twitterImage.setAttribute("name", "twitter:image");
        document.head.appendChild(twitterImage);
      }
      twitterImage.setAttribute("content", settings.og_image_url);
    }

    // Update Twitter title
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (settings.site_title) {
      if (!twitterTitle) {
        twitterTitle = document.createElement("meta");
        twitterTitle.setAttribute("name", "twitter:title");
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute("content", settings.site_title);
    }

    // Update Twitter description
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (settings.site_description) {
      if (!twitterDescription) {
        twitterDescription = document.createElement("meta");
        twitterDescription.setAttribute("name", "twitter:description");
        document.head.appendChild(twitterDescription);
      }
      twitterDescription.setAttribute("content", settings.site_description);
    }

    // Update Twitter site handle
    let twitterSite = document.querySelector('meta[name="twitter:site"]');
    if (settings.twitter_handle) {
      if (!twitterSite) {
        twitterSite = document.createElement("meta");
        twitterSite.setAttribute("name", "twitter:site");
        document.head.appendChild(twitterSite);
      }
      twitterSite.setAttribute("content", settings.twitter_handle);
    }

    // Update favicon with Safari-safe local PNG fallbacks
    const cmsFaviconUrl = settings.favicon_url?.trim();
    const canUseCmsIcon = Boolean(cmsFaviconUrl && isSameOriginOrRelativeIcon(cmsFaviconUrl));

    upsertIconLink({
      selector: 'link[rel="icon"]:not([sizes])',
      rel: "icon",
      href: getVersionedIconUrl(canUseCmsIcon ? cmsFaviconUrl! : STATIC_FAVICON_ASSETS.generic),
      type: "image/png",
    });

    upsertIconLink({
      selector: 'link[rel="shortcut icon"]',
      rel: "shortcut icon",
      href: getVersionedIconUrl(canUseCmsIcon ? cmsFaviconUrl! : STATIC_FAVICON_ASSETS.shortcut),
      type: "image/png",
    });

    upsertIconLink({
      selector: 'link[rel="icon"][sizes="32x32"]',
      rel: "icon",
      href: getVersionedIconUrl(canUseCmsIcon ? cmsFaviconUrl! : STATIC_FAVICON_ASSETS.icon32),
      sizes: "32x32",
      type: "image/png",
    });

    upsertIconLink({
      selector: 'link[rel="icon"][sizes="16x16"]',
      rel: "icon",
      href: getVersionedIconUrl(canUseCmsIcon ? cmsFaviconUrl! : STATIC_FAVICON_ASSETS.icon16),
      sizes: "16x16",
      type: "image/png",
    });

    upsertIconLink({
      selector: 'link[rel="apple-touch-icon"]',
      rel: "apple-touch-icon",
      href: getVersionedIconUrl(canUseCmsIcon ? cmsFaviconUrl! : STATIC_FAVICON_ASSETS.appleTouch),
      sizes: "180x180",
      type: "image/png",
    });

    upsertIconLink({
      selector: 'link[rel="apple-touch-icon-precomposed"]',
      rel: "apple-touch-icon-precomposed",
      href: getVersionedIconUrl(canUseCmsIcon ? cmsFaviconUrl! : STATIC_FAVICON_ASSETS.appleTouchPrecomposed),
      sizes: "180x180",
      type: "image/png",
    });

    document
      .querySelectorAll('link[rel="mask-icon"], link[rel="icon"][type="image/x-icon"], link[rel="shortcut icon"][type="image/x-icon"]')
      .forEach((legacyIcon) => legacyIcon.remove());
  }, [settings]);

  return null;
}
